import { useCallback, useEffect, useRef } from 'react';

/**
 * Лента, которая держится у последнего сообщения (02.10.2026, чат в сборке 1.1.1).
 *
 * ⚠️ ЧТО БЫЛО. На любое изменение ленты экран плавно ехал вниз: пришло сообщение, пока читаешь старое, — и тебя
 * уносило от места чтения. А когда область сжималась (открылась клавиатура), лента не ехала вовсе: последнее
 * сообщение оставалось под полем ввода.
 *
 * Правило: если человек и так был внизу — низ остаётся низом, что бы ни изменилось (новое сообщение, высота
 * области). Читает старое — не трогаем. `stickToBottom()` возвращает к низу принудительно: своя отправка.
 */

/** Ближе этого к низу — «был внизу», пиксели. */
export const NEAR_BOTTOM_PX = 80;

export function isNearBottom(el: { scrollHeight: number; scrollTop: number; clientHeight: number }): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
}

export function useStickToBottom(deps: readonly unknown[]) {
  const listRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);

  const toBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const onListScroll = useCallback(() => {
    const el = listRef.current;
    if (el) atBottom.current = isNearBottom(el);
  }, []);

  const stickToBottom = useCallback(() => {
    atBottom.current = true;
  }, []);

  useEffect(() => {
    if (atBottom.current) toBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Область стала ниже или выше (клавиатура, поворот) — низ остаётся низом.
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (atBottom.current) toBottom();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [toBottom]);

  return { listRef, onListScroll, stickToBottom };
}
